import { Grid } from '@design-systems/canvas-ui'
import { ErrorAlert } from 'components/error-fallback'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { BasicPlaceholder } from 'components/placeholder'
import {
    useGetTheftRGDExternal,
    useGetTheftRGDInternal,
} from 'hooks/assets-protection/useTheftRGD'
import useCountdown from 'hooks/utils/useCountdown'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useCallback } from 'react'
import { Cell, TableData } from 'utils/data'
import { buildDataForTheftRDG } from '../helpers'
import { TRANSLATION_KEYS } from 'locales/translation-keys'

export const TheftRGD = () => {
    const { t, currentLocation, env, session, logout, isEnglish } =
        useEssentials()
    const { assetsProtection } = TRANSLATION_KEYS
    const { filters } = assetsProtection

    const handleDrillUpdate = useCallback((newDrills: string[]) => {}, [])
    const internalHeaders = [
        t(assetsProtection.headers.internal.loc),
        t(assetsProtection.headers.internal.merchCases),
        t(assetsProtection.headers.internal.merchCasesBPLY),
        t(assetsProtection.headers.internal.posOtherCases),
        t(assetsProtection.headers.internal.posOtherCasesBPLY),
        t(assetsProtection.headers.internal.daysToClose),
    ]
    const externalHeaders = [
        t(assetsProtection.headers.external.loc),
        t(assetsProtection.headers.external.detainedApps),
        t(assetsProtection.headers.external.KTRToAppPercent),
        t(assetsProtection.headers.external.externalAppProductivity),
        t(assetsProtection.headers.external.averageAppDollars),
        t(assetsProtection.headers.external.npiRate),
    ]
    const internalTitle = t(assetsProtection.internal)
    const externalTitle = t(assetsProtection.external)

    const {
        data: internalData,
        isLoading: isInternalLoading,
        isError: isInternalError,
        error,
        refetch: refetchInternal,
    } = useGetTheftRGDInternal({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })

    const {
        data: externalData,
        isLoading: isExternalLoading,
        isError: isExternalError,
        refetch: refetchExternal,
    } = useGetTheftRGDExternal({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })
    let tableData =
        internalData && externalData
            ? buildDataForTheftRDG(
                  internalData,
                  externalData,
                  currentLocation,
                  internalHeaders,
                  externalHeaders,
                  internalTitle,
                  externalTitle,
              )
            : undefined
    const count = useCountdown(5, logout, error as Error)

    const handleRetry = useCallback(() => {
        if (isInternalError) {
            void refetchInternal()
        }
        if (isExternalError) {
            void refetchExternal()
        }
    }, [isInternalError, isExternalError, refetchInternal, refetchExternal])

    if (isInternalLoading || isExternalLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={5} columns={6} />
            </div>
        )
    }

    if (isInternalError || isExternalError) {
        return <ErrorAlert error={error} count={count} onRetry={handleRetry} />
    }

    const pickColumns = (
        data: TableData,
        columnIndexes: number[],
    ): TableData => {
        const newData = { ...data }
        newData.rows = newData.rows.map((row) =>
            columnIndexes.map((index) => row[index]),
        )
        newData.footer = columnIndexes
            .map((index) => data.footer?.[index])
            .filter((heading): heading is Cell => heading !== undefined)

        return newData
    }

    return (
        <Grid.Container>
            <Grid.Item
                size={{
                    xs: 12,
                }}
            >
                {tableData
                    ?.filter((titledTable) => titledTable.table !== undefined)
                    .map((titledTable) => {
                        const { title, table } = titledTable
                        if (!table) {
                            return null
                        }

                        const columnConfig = {
                            internal: {
                                percentView: [0, 2, 3, 7, 8, 11],
                                dollarView: [0, 4, 5, 10, 9, 11],
                            },
                            external: {
                                percentView: [0, 1, 13, 5, 14, 15],
                                dollarView: [0, 3, 13, 5, 14, 15],
                            },
                        }
                        const config =
                            title === internalTitle
                                ? columnConfig.internal
                                : columnConfig.external

                        const payload = {
                            [t(filters.viewInPercent)]: [
                                {
                                    title: '',
                                    subtitle: '',
                                    data: {
                                        tab: pickColumns(
                                            table,
                                            config.percentView,
                                        ),
                                    },
                                },
                            ],
                            [t(filters.viewInDollars)]: [
                                {
                                    title: '',
                                    subtitle: '',
                                    data: {
                                        tab: pickColumns(
                                            table,
                                            config.dollarView,
                                        ),
                                    },
                                },
                            ],
                        }

                        return (
                            <GeneralizedComponent
                                payload={payload}
                                title={title}
                                titleClassName={'hc8-fs-150'}
                                isLoading={isInternalLoading}
                                scrollableWidth={true}
                                widths={
                                    isEnglish
                                        ? [75, 70, 90, 90, 100, 90]
                                        : [85, 80, 85, 110, 170, 90]
                                }
                                key={title}
                                onDrillUpdate={handleDrillUpdate}
                                kpiName="assets protection theft non-store"
                            />
                        )
                    })}
            </Grid.Item>
        </Grid.Container>
    )
}
